/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import { useViewedScreen } from "@packages/analytics";
import type { Prayer } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  createModal,
  cssMediaQueryMinWidthTablet,
  END_OF_BROWSER_ASYNC_QUEUE,
  FullscreenModalModal,
  HallowImage,
  Mask,
  radius,
  showModal,
  spacing,
  Text,
  useIsDesktopViewport,
  useModal,
  zIndices,
} from "@packages/ui";
import { useMemo } from "react";

import { useRequestMeRelatedAccounts } from "../requests/useRequestMeRelatedAccounts";
import { EnterPin, EnterPinType } from "./EnterPin";

const lockedMobileModal: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.primary,
  borderRadius: theme.radius.none,
  paddingBottom: theme.spacing.l,
  paddingInline: theme.spacing.xl,
});

const lockedDesktopModal: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.neutralsHigher,
  borderRadius: theme.radius.m,
  height: "90svh",
  width: "90svw",
});
const lockedDesktopModalBackground: Interpolation<{
  backgroundColor: string;
}> = ({ backgroundColor }) => ({
  backgroundColor: `color-mix(in srgb, ${backgroundColor}, #000000 60%)`,
});

const gridCss: Interpolation<ThemeType> = (theme) => ({
  display: "grid",
  gap: theme.spacing.xl,
  gridTemplateAreas: `"image" "text" "button"`,
  gridTemplateColumns: "1fr",
  gridTemplateRows: "minmax(40vh, 67vw) 1fr min-content",
  height: theme.spacing.full,
  width: theme.spacing.full,
  [cssMediaQueryMinWidthTablet]: {
    alignContent: "center",
    columnGap: "56px",
    gridTemplateAreas: `"image text" "image button"`,
    gridTemplateColumns: "400px 400px",
    gridTemplateRows: "min-content min-content",
    height: theme.spacing.full,
    justifyContent: "center",
    maxHeight: "unset",
    maxWidth: "unset",
    paddingBlock: "200px",
    paddingInline: "160px",
    rowGap: theme.spacing.l,
    width: theme.spacing.full,
  },
});

const imageContainerCss: Interpolation = {
  aspectRatio: 1,
  borderRadius: radius.m,
  gridArea: "image",
  height: spacing.full,
  margin: "auto",
  overflow: "hidden",
  position: "relative",
  width: spacing.full,
  maxWidth: "50vh",
};

const imageCss: Interpolation = {
  width: spacing.full,
  height: spacing.full,
  objectFit: "cover",
  inset: 0,
};

const maskCss: Interpolation = {
  position: "absolute",
  bottom: 0,
  zIndex: zIndices.mask,
};

const titleContainerCss: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  bottom: 26,
  color: theme.constantSemanticColors.onColorHighest,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  left: 26,
  position: "absolute",
  right: 26,
  zIndex: theme.zIndices.maskOver,
  gap: theme.spacing.xs,
});

const subtitleContainerCss: Interpolation<ThemeType> = (theme) => ({
  color: theme.constantSemanticColors.onColorVeryHigh,
});

const textContainerCss: Interpolation<ThemeType> = (theme) => ({
  gridArea: "text",
  color: theme.semanticColors.secondary,
});

const lockedTitleCss: Interpolation = {
  marginBlockStart: spacing.l,
  marginBlockEnd: spacing.m,
};

const buttonCss: Interpolation = {
  gridArea: "button",
  maxWidth: "50vh",
};

type LockedContentProps = Pick<Prayer, "images" | "title"> & {
  onSuccess: () => void;
  onFailure: () => void;
};

export const LockedContent = createModal(
  ({ title, images }: LockedContentProps) => {
    useViewedScreen({ screen: "content_is_locked" });
    const isDesktop = useIsDesktopViewport();
    const modal = useModal();
    const { data: relatedAccounts } = useRequestMeRelatedAccounts();
    const parentId = useMemo(
      () => relatedAccounts?.find((acc) => !acc.is_kid)?.id ?? null,
      [relatedAccounts],
    );

    return (
      <FullscreenModalModal
        open={modal.visible}
        onOpenChange={(open) => {
          if (open) return modal.show();
          modal.resolve(false);
          modal.remove();
        }}
        fullScreenModalProps={{
          contentCSS: lockedMobileModal,
          showCloseButton: true,
          contentProps: {
            onInteractOutside: (event) => event.preventDefault(),
          },
          closeProps: {
            variant: "transparentWhite",
          },
        }}
        modalProps={{
          contentProps: {
            onInteractOutside: (event) => event.preventDefault(),
          },
          contentCSS: [
            lockedDesktopModal,
            lockedDesktopModalBackground({ backgroundColor: images.color_hex }),
          ],
          overlayCSS: { background: "transparent" },
          showClose: true,
          closeProps: {
            variant: "transparentWhite",
          },
        }}
        css={gridCss}
        wrapContentInScrollArea={false}
        trigger={null}
      >
        <div css={imageContainerCss}>
          <HallowImage
            key="image"
            alt={title}
            size="l"
            src={images}
            loading="eager"
            css={imageCss}
          />
          <Mask
            key="mask"
            color={images.color_hex as `#${string}`}
            direction="to top"
            size="xl"
            css={maskCss}
          />
          <div css={titleContainerCss}>
            <Text type="title" size={isDesktop ? "l" : "s"}>
              {title}
            </Text>
            <Text
              type={isDesktop ? "button" : "detail"}
              size="l"
              css={subtitleContainerCss}
            >
              <I18nMessage message="general_words_available_with_a_hallow_subscription" />
            </Text>
          </div>
        </div>
        <div css={textContainerCss}>
          <Text
            type="headline"
            size={isDesktop ? "l" : "s"}
            as="h2"
            css={lockedTitleCss}
          >
            <I18nMessage message="kids_locked_content_title" />
          </Text>
          <Text size="l">
            <I18nMessage message="kids_locked_content_subtitle" />
          </Text>
        </div>
        <Button
          size="l"
          variant="secondary"
          css={buttonCss}
          isFullWidth
          onClick={async () => {
            let success = false;
            await showModal(EnterPin, {
              onSuccess: () => {
                success = true;
              },
              type: EnterPinType.SwitchProfile,
              userId: parentId,
            });
            setTimeout(() => {
              modal.resolve(success);
              modal.remove();
            }, END_OF_BROWSER_ASYNC_QUEUE);
          }}
        >
          <I18nMessage message="enter_parent_pin_title" />
        </Button>
      </FullscreenModalModal>
    );
  },
);

LockedContent.displayName = "LockedContent";
